import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import { accounts as accountsApi, users, billing as billingApi } from "../lib/supabase.js";

const TIERS = { starter: { name: "Starter", price: 99, limit: 500 }, pro: { name: "Pro", price: 299, limit: 2500 }, enterprise: { name: "Enterprise", price: 599, limit: 999999 } };
const color = "#6366f1";

export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Discount form
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountNote, setDiscountNote] = useState("");

  // Limit form
  const [customLimit, setCustomLimit] = useState(500);

  // Plan form
  const [selectedPlan, setSelectedPlan] = useState("");

  const reload = async () => {
    const { data } = await accountsApi.get(id);
    setAccount(data);
    if (data) {
      const { data: members } = await users.listForAccount(data.id);
      setTeam(members || []);
      const b = data.billing?.[0];
      setBillingData(b);
      setDiscountPercent(b?.discount_percent || 0);
      setDiscountNote(b?.discount_note || "");
      setCustomLimit(b?.conversation_limit || 500);
      setSelectedPlan(b?.plan || data.plan || "starter");
    }
    setLoading(false);
  };

  useEffect(() => { reload(); }, [id]);

  const flash = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage(null), 4000); };

  const handleApplyDiscount = async () => {
    setSaving(true);
    const { error } = await billingApi.applyDiscount(account.id, discountPercent, discountNote);
    if (error) flash("error", error.message); else { flash("success", `Discount ${discountPercent}% applied.`); reload(); }
    setSaving(false);
  };

  const handleRemoveDiscount = async () => {
    setSaving(true);
    const { error } = await billingApi.applyDiscount(account.id, 0, null);
    if (error) flash("error", error.message); else { setDiscountPercent(0); setDiscountNote(""); flash("success", "Discount removed."); reload(); }
    setSaving(false);
  };

  const handleSetLimit = async () => {
    setSaving(true);
    const { error } = await billingApi.setConversationLimit(account.id, customLimit);
    if (error) flash("error", error.message); else { flash("success", `Limit set to ${customLimit.toLocaleString()}.`); reload(); }
    setSaving(false);
  };

  const handleResetUsage = async () => {
    if (!confirm("Reset conversation count to 0?")) return;
    setSaving(true);
    const { error } = await billingApi.resetUsage(account.id);
    if (error) flash("error", error.message); else { flash("success", "Usage reset."); reload(); }
    setSaving(false);
  };

  const handleChangePlan = async () => {
    setSaving(true);
    const tier = TIERS[selectedPlan];
    const { error } = await billingApi.update(account.id, { plan: selectedPlan, conversation_limit: tier?.limit || 500, mrr_cents: (tier?.price || 99) * 100 });
    if (!error) await accountsApi.update(account.id, { plan: selectedPlan, conversation_limit: tier?.limit || 500 });
    if (error) flash("error", error.message); else { flash("success", `Plan changed to ${tier?.name || selectedPlan}.`); reload(); }
    setSaving(false);
  };

  if (loading) return <Layout><div style={{ color: "#555", padding: 40 }}>Loading...</div></Layout>;
  if (!account) return <Layout><div style={{ color: "#555", padding: 40 }}>Account not found.</div></Layout>;

  const a = account;
  const b = billingData;
  const usagePercent = b ? Math.min(100, Math.round(((b.conversations_used || 0) / (b.conversation_limit || 500)) * 100)) : 0;

  const iS = { padding: "8px 12px", background: "#0a0a10", border: "1px solid #1e1e2e", borderRadius: 6, color: "#ccc", fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
  const lS = { display: "block", fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, fontWeight: 700 };
  const secS = { background: "#0a0a0f", border: "1px solid #1a1a2e", borderRadius: 12, padding: 24, marginBottom: 20 };
  const secT = (t) => <h3 style={{ margin: "0 0 16px", fontSize: 14, color: "#888", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>{t}</h3>;
  const btnS = (bg) => ({ padding: "8px 18px", background: bg || color, border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: saving ? 0.5 : 1 });

  return (
    <Layout>
      <button onClick={() => navigate("/accounts")} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", color, fontSize: 13, cursor: "pointer", marginBottom: 20, padding: 0, fontWeight: 600, fontFamily: "inherit" }}>← All Accounts</button>

      {message && <div style={{ padding: "10px 16px", borderRadius: 8, marginBottom: 16, fontSize: 12, background: message.type === "error" ? "#2a0a0a" : "#0a2e1a", color: message.type === "error" ? "#f87171" : "#22c55e", border: `1px solid ${message.type === "error" ? "#991b1b" : "#166534"}` }}>{message.text}</div>}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 32 }}>
        <div style={{ width: 52, height: 52, borderRadius: 12, background: `${a.widget_color || color}20`, border: `1px solid ${a.widget_color || color}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, color: a.widget_color || color }}>{a.name?.[0]}</div>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{a.name}</h1>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4, fontSize: 12, color: "#555" }}>
            <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: "uppercase", background: a.status === "active" ? "#0a2e1a" : "#2a1f05", color: a.status === "active" ? "#34d399" : "#fbbf24" }}>{a.status}</span>
            <span>{a.domain || a.slug}</span>
            <span>·</span>
            <span>Since {new Date(a.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* ─── LEFT COLUMN ─── */}
        <div>
          {/* Billing Overview */}
          <div style={secS}>
            {secT("💳 Billing Overview")}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              {[
                { label: "Plan", value: TIERS[b?.plan || a.plan]?.name || (b?.plan || a.plan), c: color },
                { label: "Status", value: b?.status || "pending", c: b?.status === "active" ? "#22c55e" : "#f59e0b" },
                { label: "MRR", value: `$${(b?.mrr_cents || 0) / 100}/mo`, c: "#38bdf8" },
                { label: "Stripe Customer", value: b?.stripe_customer_id ? "✓ Connected" : "Not connected", c: b?.stripe_customer_id ? "#22c55e" : "#555" },
              ].map((card) => (
                <div key={card.label} style={{ padding: "10px 14px", background: "#0d0d14", borderRadius: 8, border: "1px solid #1a1a2a" }}>
                  <div style={{ fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, marginBottom: 4 }}>{card.label}</div>
                  <div style={{ fontSize: 14, color: card.c, fontWeight: 700 }}>{card.value}</div>
                </div>
              ))}
            </div>

            {/* Usage meter */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: "#888" }}>Conversations this period</span>
                <span style={{ fontSize: 11, color: usagePercent > 80 ? "#f59e0b" : "#888", fontWeight: 600 }}>{(b?.conversations_used || 0).toLocaleString()} / {(b?.conversation_limit || 500) >= 999999 ? "Unlimited" : (b?.conversation_limit || 500).toLocaleString()}</span>
              </div>
              <div style={{ height: 6, background: "#1a1a2a", borderRadius: 3 }}>
                <div style={{ height: 6, borderRadius: 3, background: usagePercent > 90 ? "#ef4444" : usagePercent > 70 ? "#f59e0b" : color, width: `${usagePercent}%`, transition: "width 0.3s" }} />
              </div>
            </div>

            {b?.current_period_start && <div style={{ fontSize: 11, color: "#444" }}>Period: {new Date(b.current_period_start).toLocaleDateString()} — {new Date(b.current_period_end).toLocaleDateString()}</div>}
            {b?.discount_percent > 0 && <div style={{ fontSize: 12, color: "#22c55e", marginTop: 6 }}>🏷 {b.discount_percent}% discount{b.discount_note ? ` — ${b.discount_note}` : ""}</div>}
          </div>

          {/* Change Plan */}
          <div style={secS}>
            {secT("📋 Change Plan")}
            <p style={{ fontSize: 11, color: "#555", margin: "0 0 12px" }}>This updates the database record. For Stripe subscription changes, use the Stripe dashboard.</p>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {Object.entries(TIERS).map(([key, tier]) => (
                <button key={key} onClick={() => setSelectedPlan(key)} style={{ flex: 1, padding: "10px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textAlign: "center", background: selectedPlan === key ? `${color}15` : "#0d0d14", color: selectedPlan === key ? color : "#666", border: `1px solid ${selectedPlan === key ? `${color}40` : "#1a1a2a"}` }}>
                  {tier.name}<br /><span style={{ fontSize: 10, fontWeight: 400 }}>${tier.price}/mo</span>
                </button>
              ))}
            </div>
            <button onClick={handleChangePlan} disabled={saving} style={btnS()}>Apply Plan Change</button>
          </div>

          {/* Config */}
          <div style={secS}>
            {secT("⚙ Configuration")}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[
                { label: "AI Provider", value: `${a.ai_provider} / ${a.ai_model}` },
                { label: "Widget Color", value: a.widget_color, isColor: true },
                { label: "Bot Name", value: a.widget_bot_name || "AI Assistant" },
                { label: "Supabase Project", value: a.supabase_project_id || "Not provisioned" },
              ].map(({ label, value, isColor }) => (
                <div key={label}>
                  <div style={lS}>{label}</div>
                  <div style={{ fontSize: 13, color: "#ccc", display: "flex", alignItems: "center", gap: 6 }}>
                    {isColor && <span style={{ width: 12, height: 12, borderRadius: 3, background: value, display: "inline-block" }} />}
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ─── RIGHT COLUMN ─── */}
        <div>
          {/* Apply Discount */}
          <div style={secS}>
            {secT("🏷 Discount")}
            <p style={{ fontSize: 11, color: "#555", margin: "0 0 12px" }}>Apply a percentage discount. This is recorded in your database and shown to the tenant. To apply a Stripe coupon, use the Stripe dashboard.</p>
            <div style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "flex-end" }}>
              <div>
                <label style={lS}>Discount %</label>
                <input type="number" value={discountPercent} onChange={(e) => setDiscountPercent(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))} min={0} max={100} style={{ ...iS, width: 80 }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lS}>Note (visible to tenant)</label>
                <input value={discountNote} onChange={(e) => setDiscountNote(e.target.value)} placeholder="e.g., Founding client, Early adopter..." style={{ ...iS, width: "100%" }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleApplyDiscount} disabled={saving} style={btnS()}>Apply Discount</button>
              {(b?.discount_percent || 0) > 0 && <button onClick={handleRemoveDiscount} disabled={saving} style={btnS("#555")}>Remove Discount</button>}
            </div>
            {/* Quick presets */}
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              {[
                { label: "10%", pct: 10, note: "Early adopter" },
                { label: "25%", pct: 25, note: "Partner discount" },
                { label: "50%", pct: 50, note: "Founding client" },
                { label: "100% (Free)", pct: 100, note: "Internal / free tier" },
              ].map((p) => (
                <button key={p.label} onClick={() => { setDiscountPercent(p.pct); setDiscountNote(p.note); }} style={{ padding: "4px 10px", background: "#0d0d14", border: "1px solid #1a1a2a", borderRadius: 4, color: "#888", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>{p.label}</button>
              ))}
            </div>
          </div>

          {/* Conversation Limit */}
          <div style={secS}>
            {secT("💬 Conversation Limit")}
            <p style={{ fontSize: 11, color: "#555", margin: "0 0 12px" }}>Override the plan's default limit. Set to 999999 for unlimited.</p>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 12 }}>
              <div>
                <label style={lS}>Monthly Limit</label>
                <input type="number" value={customLimit} onChange={(e) => setCustomLimit(parseInt(e.target.value) || 0)} min={0} style={{ ...iS, width: 120 }} />
              </div>
              <button onClick={handleSetLimit} disabled={saving} style={btnS()}>Set Limit</button>
              <button onClick={handleResetUsage} disabled={saving} style={btnS("#f59e0b")}>Reset Count</button>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {[500, 1000, 2500, 5000, 999999].map((l) => (
                <button key={l} onClick={() => setCustomLimit(l)} style={{ padding: "4px 10px", background: customLimit === l ? `${color}15` : "#0d0d14", border: `1px solid ${customLimit === l ? `${color}40` : "#1a1a2a"}`, borderRadius: 4, color: customLimit === l ? color : "#888", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>{l >= 999999 ? "Unlimited" : l.toLocaleString()}</button>
              ))}
            </div>
          </div>

          {/* Team */}
          <div style={secS}>
            {secT(`👥 Team (${team.length})`)}
            {team.length === 0 ? (
              <div style={{ fontSize: 13, color: "#444" }}>No team members yet.</div>
            ) : team.map((u) => (
              <div key={u.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #141420" }}>
                <div>
                  <div style={{ fontSize: 13, color: "#ccc" }}>{u.name || u.email}</div>
                  <div style={{ fontSize: 10, color: "#555" }}>{u.email}</div>
                </div>
                <span style={{ fontSize: 10, color: "#818cf8", fontWeight: 600, textTransform: "uppercase", padding: "3px 8px", background: "#12122a", borderRadius: 4 }}>{u.role}</span>
              </div>
            ))}
          </div>

          {/* Org Context */}
          <div style={secS}>
            {secT("📝 Organization Context")}
            <p style={{ fontSize: 12, color: "#888", lineHeight: 1.6, margin: 0 }}>{a.org_context || "No organizational context set."}</p>
          </div>
        </div>
      </div>

      {/* Embed Code */}
      <div style={secS}>
        {secT("📋 Embed Code")}
        <pre style={{ margin: 0, padding: 16, background: "#06060a", border: "1px solid #1a1a2e", borderRadius: 8, color: "#818cf8", fontSize: 12, fontFamily: "monospace", overflowX: "auto", lineHeight: 1.7 }}>{`<!-- Chat Widget -->
<script src="https://cdn.clarifico.io/widget-chat.js"
  data-account="${a.slug}"
  data-config="default"
  data-api="https://amtdvoginmkwzbkwrdld.supabase.co/functions/v1"
  async></script>

<!-- Map Widget -->
<div id="clarifico-map-dealer-map"></div>
<script src="https://cdn.clarifico.io/widget-map.js"
  data-account="${a.slug}"
  data-map="dealer-map"
  data-api="https://amtdvoginmkwzbkwrdld.supabase.co/functions/v1"
  data-container="clarifico-map-dealer-map"
  async></script>`}</pre>
      </div>
    </Layout>
  );
}
